import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { Modal } from "@/shared/ui/primitives";
import { useApiClient } from "@/shared/api/ApiClientProvider";
import type { AccessCode } from "@/shared/api/contracts";

export const ACCESS_CODE_QUERY_KEY = ["profile", "access-code"] as const;

function buildInviteLink(code: string): string {
  return `https://goliminl.com/invite/${encodeURIComponent(code)}`;
}

function invitesRemaining(code: AccessCode | null | undefined): number {
  if (!code || code.status !== "ACTIVE") return 0;
  return Math.max(0, code.usesRemaining);
}

async function copyText(value: string) {
  try {
    await navigator.clipboard.writeText(value);
    return;
  } catch {
    const el = document.createElement("textarea");
    el.value = value;
    el.style.cssText = "position:fixed;opacity:0;pointer-events:none";
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
  }
}

/** Figma `Modal / Invite modal` `1645:26086`. */
export function InviteModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const apiClient = useApiClient();
  const [copied, setCopied] = useState(false);

  const accessCodeQ = useQuery({
    queryKey: ACCESS_CODE_QUERY_KEY,
    queryFn: () => apiClient.listMyAccessCodes(),
    enabled: open,
  });

  const code = accessCodeQ.data?.code ?? null;
  const remaining = invitesRemaining(code);
  const inviteLink = code ? buildInviteLink(code.code) : "";
  const displayLink = inviteLink.replace(/^https?:\/\//, "");

  const countLabel = accessCodeQ.isPending
    ? "LOADING…"
    : accessCodeQ.isError
      ? "UNAVAILABLE"
      : remaining === 1
        ? "1 INVITE AVAILABLE"
        : `${remaining} INVITES AVAILABLE`;

  const handleCopy = async () => {
    if (!inviteLink || remaining <= 0) return;
    await copyText(inviteLink);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal
      ariaLabel="Invite to Liminl"
      className="invite-modal"
      header={<span className="sr-only">Invite to Liminl</span>}
      onOpenChange={onOpenChange}
      open={open}
    >
      <div className="invite-modal-body">
        <div aria-hidden className="invite-modal-stars">
          <span className="invite-modal-node is-sage">
            <img alt="" height={22} src="/profile/icon-star.svg" width={22} />
          </span>
          <img
            alt=""
            className="invite-modal-line"
            height={2}
            src="/profile/invite-line.svg"
            width={14}
          />
          <span className="invite-modal-node is-blue">
            <img alt="" height={22} src="/profile/icon-star.svg" width={22} />
          </span>
        </div>

        <h2 className="invite-modal-title">Invite to Liminl</h2>
        <p className="invite-modal-lead">
          Friends or family also searching? Help them build their network too.
        </p>
        <p className="invite-modal-count">{countLabel}</p>

        <div className="invite-modal-link-row">
          <span className={`invite-modal-link${inviteLink ? "" : " is-muted"}`}>
            {accessCodeQ.isPending
              ? "Preparing your invite link…"
              : accessCodeQ.isError
                ? "Your invite link couldn't load yet."
                : displayLink || "Your invite link will appear here soon."}
          </span>
          <button
            className="invite-modal-copy"
            disabled={!inviteLink || remaining <= 0}
            onClick={() => void handleCopy()}
            type="button"
          >
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>

        <p className="invite-modal-foot">
          Every person who joins makes your map denser, and your paths warmer.
        </p>
      </div>
    </Modal>
  );
}

export function InviteNavButton({ onClick }: { onClick: () => void }) {
  return (
    <button className="sidebar-invite" onClick={onClick} type="button">
      <Plus aria-hidden size={17} strokeWidth={2} />
      <span>Invite</span>
    </button>
  );
}
